/**
 * useComparisonRecords Hook
 *
 * Fetches and manages records for the comparison selector.
 * Groups records by lifecycle state with smart loading strategy.
 *
 * Features:
 * - Count-first: Fetches counts per state immediately
 * - Eager load: If total <= 50, loads all records
 * - Lazy load: Otherwise, loads on-demand per state group
 * - Pagination: "Load more" within large groups
 * - Search: Filter records by search term
 */

import { useState, useCallback, useEffect, useMemo } from 'react'
import { dataService } from '@/services/dataService'
import type { Filter } from '@/types/api.types'
import type {
  RecordGroup,
  UseComparisonRecordsResult,
  ComparisonSelectorMode,
} from './types'

/**
 * Default lifecycle state order for grouping.
 * Used when metadata doesn't specify stateOrder.
 */
const DEFAULT_STATE_ORDER = ['draft', 'active', 'expired', 'superseded', 'archived']

/**
 * Labels for lifecycle states.
 */
const STATE_LABELS: Record<string, string> = {
  draft: 'Drafts',
  active: 'Active',
  expired: 'Expired',
  superseded: 'Superseded',
  archived: 'Archived',
}

interface UseComparisonRecordsOptions {
  /** API endpoint (e.g., '/offerings') */
  endpoint: string
  /** The anchor record for same-entity comparisons */
  anchorRecord: Record<string, unknown> | null
  /** Selector mode: 'same_entity' or 'any_record' */
  selectorMode: ComparisonSelectorMode
  /** Threshold for eager loading (default: 50) */
  maxVersionsEager?: number
  /** Page size for pagination (default: 20) */
  paginationSize?: number
  /** Custom state order from metadata */
  stateOrder?: string[]
  /** Whether to enable fetching */
  enabled?: boolean
}

/**
 * Hook for fetching and managing comparison records.
 *
 * @param options - Configuration options
 * @returns Records grouped by state with loading/search controls
 *
 * @example
 * ```tsx
 * const {
 *   groups,
 *   totalCount,
 *   isLoadingCounts,
 *   loadMoreForGroup,
 *   searchRecords,
 * } = useComparisonRecords({
 *   endpoint: '/offerings',
 *   anchorRecord: currentRecord,
 *   selectorMode: 'same_entity',
 * })
 * ```
 */
export function useComparisonRecords({
  endpoint,
  anchorRecord,
  selectorMode,
  maxVersionsEager = 50,
  paginationSize = 20,
  stateOrder = DEFAULT_STATE_ORDER,
  enabled = true,
}: UseComparisonRecordsOptions): UseComparisonRecordsResult {
  // State
  const [groups, setGroups] = useState<RecordGroup[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [isLoadingCounts, setIsLoadingCounts] = useState(false)
  const [isLoadingRecords, setIsLoadingRecords] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [searchTerm, setSearchTerm] = useState('')

  // Get the table name from endpoint
  const tableName = useMemo(
    () => (endpoint.startsWith('/') ? endpoint.slice(1) : endpoint),
    [endpoint]
  )

  // Base filters for all queries
  const baseFilters = useMemo<Filter[]>(() => {
    const filters: Filter[] = [
      { key: 'deletedAt', constraint_type: 'IS_NULL' },
    ]

    // Exclude anchor record
    if (anchorRecord?.uid) {
      filters.push({
        key: 'uid',
        constraint_type: 'NOT_EQUAL',
        value: anchorRecord.uid as string,
      })
    }

    // For same_entity mode, filter by keyComboHash
    if (selectorMode === 'same_entity' && anchorRecord?.keyComboHash) {
      filters.push({
        key: 'keyComboHash',
        constraint_type: 'EQUAL',
        value: anchorRecord.keyComboHash as string,
      })
    }

    return filters
  }, [anchorRecord, selectorMode])

  // Search filters
  const searchFilters = useMemo<Filter[]>(() => {
    if (!searchTerm.trim()) return []

    // Search in name field (most common)
    // TODO: Make searchable fields configurable
    return [
      {
        constraint_type: 'OR',
        filters: [
          { key: 'name', constraint_type: 'CONTAINS', value: searchTerm.trim() },
          { key: 'uid', constraint_type: 'CONTAINS', value: searchTerm.trim() },
        ],
      },
    ]
  }, [searchTerm])

  // Fetch counts per lifecycle state
  const fetchCounts = useCallback(async () => {
    if (!enabled || !anchorRecord) return

    setIsLoadingCounts(true)
    setError(null)

    try {
      // Fetch count for each lifecycle state
      const countPromises = stateOrder.map(async (state) => {
        const filters: Filter[] = [
          ...baseFilters,
          ...searchFilters,
          { key: 'lifecycleState', constraint_type: 'EQUAL', value: state },
        ]

        const response = await dataService.search(tableName, {
          filters,
          limit: 0, // Just get count
        })

        return {
          state,
          count: response.meta?.total ?? 0,
        }
      })

      const counts = await Promise.all(countPromises)
      const total = counts.reduce((sum, c) => sum + c.count, 0)
      setTotalCount(total)

      // Initialize groups with counts
      const initialGroups: RecordGroup[] = counts
        .filter((c) => c.count > 0) // Only include states with records
        .map((c) => ({
          state: c.state,
          label: STATE_LABELS[c.state] || c.state,
          count: c.count,
          records: [],
          isFullyLoaded: false,
          isExpanded: c.state === 'draft' || c.state === 'active', // Expand draft/active by default
        }))

      setGroups(initialGroups)

      // If total is small enough, eager load all records
      if (total > 0 && total <= maxVersionsEager) {
        await loadAllRecords(initialGroups)
      }
    } catch (err) {
      console.error('[useComparisonRecords] Failed to fetch counts:', err)
      setError(err instanceof Error ? err : new Error('Failed to fetch record counts'))
    } finally {
      setIsLoadingCounts(false)
    }
  }, [enabled, anchorRecord, tableName, baseFilters, searchFilters, stateOrder, maxVersionsEager])

  // Load all records for all groups (eager loading)
  const loadAllRecords = useCallback(
    async (currentGroups: RecordGroup[]) => {
      setIsLoadingRecords(true)

      try {
        const updatedGroups = await Promise.all(
          currentGroups.map(async (group) => {
            const filters: Filter[] = [
              ...baseFilters,
              ...searchFilters,
              { key: 'lifecycleState', constraint_type: 'EQUAL', value: group.state },
            ]

            const response = await dataService.search(tableName, {
              filters,
              order_by: [{ key: 'modifiedAt', direction: 'desc' }],
              limit: maxVersionsEager,
            })

            return {
              ...group,
              records: (response.data ?? []) as Record<string, unknown>[],
              isFullyLoaded: true,
            }
          })
        )

        setGroups(updatedGroups)
      } catch (err) {
        console.error('[useComparisonRecords] Failed to load all records:', err)
        setError(err instanceof Error ? err : new Error('Failed to load records'))
      } finally {
        setIsLoadingRecords(false)
      }
    },
    [tableName, baseFilters, searchFilters, maxVersionsEager]
  )

  // Load more records for a specific state group
  const loadMoreForGroup = useCallback(
    async (state: string) => {
      const groupIndex = groups.findIndex((g) => g.state === state)
      if (groupIndex === -1) return

      const group = groups[groupIndex]
      if (group.isFullyLoaded) return

      setIsLoadingRecords(true)

      try {
        const filters: Filter[] = [
          ...baseFilters,
          ...searchFilters,
          { key: 'lifecycleState', constraint_type: 'EQUAL', value: state },
        ]

        const response = await dataService.search(tableName, {
          filters,
          order_by: [{ key: 'modifiedAt', direction: 'desc' }],
          limit: paginationSize,
          offset: group.records.length,
        })

        const newRecords = (response.data ?? []) as Record<string, unknown>[]
        const allRecords = [...group.records, ...newRecords]
        const isFullyLoaded = allRecords.length >= group.count

        setGroups((prev) =>
          prev.map((g, i) =>
            i === groupIndex
              ? { ...g, records: allRecords, isFullyLoaded }
              : g
          )
        )
      } catch (err) {
        console.error('[useComparisonRecords] Failed to load more:', err)
        setError(err instanceof Error ? err : new Error('Failed to load more records'))
      } finally {
        setIsLoadingRecords(false)
      }
    },
    [groups, tableName, baseFilters, searchFilters, paginationSize]
  )

  // Toggle group expansion
  const toggleGroupExpanded = useCallback((state: string) => {
    setGroups((prev) =>
      prev.map((g) =>
        g.state === state ? { ...g, isExpanded: !g.isExpanded } : g
      )
    )
  }, [])

  // Search records
  const searchRecords = useCallback((term: string) => {
    setSearchTerm(term)
  }, [])

  // Fetch counts when dependencies change
  useEffect(() => {
    fetchCounts()
  }, [fetchCounts])

  // When search term changes, re-fetch
  useEffect(() => {
    if (searchTerm !== '') {
      // Debounce search
      const timer = setTimeout(() => {
        fetchCounts()
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [searchTerm, fetchCounts])

  // Refetch function for retry/refresh
  const refetch = useCallback(() => {
    setError(null)
    fetchCounts()
  }, [fetchCounts])

  // Clear error function
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return {
    groups,
    totalCount,
    isLoadingCounts,
    isLoadingRecords,
    error,
    loadMoreForGroup,
    toggleGroupExpanded,
    searchRecords,
    searchTerm,
    refetch,
    clearError,
  }
}

export default useComparisonRecords
